import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle2, AlertTriangle, XCircle, Info, X } from 'lucide-react';

const ToastContext = createContext(null);

const ICONS = { success: CheckCircle2, error: XCircle, warning: AlertTriangle, info: Info };
const COLORS = {
  success: 'var(--ok)',
  error: 'var(--danger)',
  warning: 'var(--warn)',
  info: 'var(--info)',
};

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const dismiss = useCallback((id) => setToasts((t) => t.filter((x) => x.id !== id)), []);

  const push = useCallback((message, type = 'info', ttl = 4200) => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, message, type }]);
    setTimeout(() => dismiss(id), ttl);
  }, [dismiss]);

  const api = useMemo(() => ({
    toast: push,
    success: (m) => push(m, 'success'),
    error: (m) => push(m, 'error'),
    warning: (m) => push(m, 'warning'),
    info: (m) => push(m, 'info'),
  }), [push]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div style={{
        position: 'fixed', top: 20, right: 20, zIndex: 9999,
        display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 400,
      }}>
        <AnimatePresence>
          {toasts.map((t) => {
            const Icon = ICONS[t.type] || Info;
            return (
              <motion.div
                key={t.id}
                initial={{ opacity: 0, x: 60, scale: 0.94 }}
                animate={{ opacity: 1, x: 0, scale: 1 }}
                exit={{ opacity: 0, x: 60, scale: 0.94 }}
                transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: 11,
                  padding: '13px 15px',
                  background: 'var(--ink-800)',
                  border: '1px solid var(--border)',
                  borderLeft: `3px solid ${COLORS[t.type]}`,
                  borderRadius: 'var(--r)',
                  boxShadow: 'var(--shadow)',
                }}
              >
                <Icon size={17} color={COLORS[t.type]} style={{ flexShrink: 0, marginTop: 1 }} />
                <span style={{ flex: 1, fontSize: 13.5, lineHeight: 1.5 }}>{t.message}</span>
                <button onClick={() => dismiss(t.id)} style={{ color: 'var(--text-lo)', display: 'flex' }} aria-label="Dismiss">
                  <X size={14} />
                </button>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}
